import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Security as SecurityIcon,
  QrCode2 as QrCodeIcon,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

export default function MfaSetup() {
  const { user } = useAuth();
  const [mfaStatus, setMfaStatus] = useState({
    mfaEnabled: false,
    mfaVerified: false,
    mfaRequired: false,
  });
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState({ type: '', message: '' });

  // Setup dialog
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [setupLoading, setSetupLoading] = useState(false);

  // Disable dialog
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');

  useEffect(() => {
    fetchMfaStatus();
  }, []);

  const fetchMfaStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/mfa/status`, {
        withCredentials: true,
      });
      setMfaStatus(response.data);
    } catch (error) {
      console.error('Error fetching MFA status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartSetup = async () => {
    setSetupLoading(true);
    setSaveStatus({ type: '', message: '' });
    try {
      const response = await axios.post(`${API_URL}/api/mfa/setup`, {}, {
        withCredentials: true,
      });
      setSetupData(response.data);
      setVerifyCode('');
      setSetupDialogOpen(true);
    } catch (error) {
      setSaveStatus({
        type: 'error',
        message: error.response?.data?.message || 'Failed to start MFA setup',
      });
    } finally {
      setSetupLoading(false);
    }
  };

  const handleVerifySetup = async () => {
    if (verifyCode.length !== 6) {
      setSaveStatus({ type: 'error', message: 'Please enter a 6-digit code' });
      return;
    }

    setSetupLoading(true);
    try {
      await axios.post(`${API_URL}/api/mfa/verify-setup`, { token: verifyCode }, {
        withCredentials: true,
      });
      setSetupDialogOpen(false);
      setSetupData(null);
      setSaveStatus({ type: 'success', message: 'MFA enabled successfully! You will need to enter a code on each login.' });
      fetchMfaStatus();
    } catch (error) {
      setSaveStatus({
        type: 'error',
        message: error.response?.data?.message || 'Invalid verification code',
      });
    } finally {
      setSetupLoading(false);
    }
  };

  const handleDisableMfa = async () => {
    if (!disablePassword) {
      setSaveStatus({ type: 'error', message: 'Password is required to disable MFA' });
      return;
    }

    setSetupLoading(true);
    try {
      await axios.post(`${API_URL}/api/mfa/disable`, { password: disablePassword }, {
        withCredentials: true,
      });
      setDisableDialogOpen(false);
      setDisablePassword('');
      setSaveStatus({ type: 'success', message: 'MFA disabled successfully' });
      fetchMfaStatus();
    } catch (error) {
      setSaveStatus({
        type: 'error',
        message: error.response?.data?.message || 'Failed to disable MFA',
      });
    } finally {
      setSetupLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress sx={{ color: '#22d3ee' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <SecurityIcon sx={{ color: '#4caf50', fontSize: 28 }} />
            <Box>
              <Typography variant="h6" sx={{ color: '#22d3ee' }}>
                Two-Factor Authentication (MFA)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Add an extra layer of security with a time-based one-time passcode (TOTP)
              </Typography>
            </Box>
          </Box>

          {saveStatus.message && (
            <Alert
              severity={saveStatus.type}
              sx={{ mb: 3 }}
              onClose={() => setSaveStatus({ type: '', message: '' })}
            >
              {saveStatus.message}
            </Alert>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Typography variant="body1">Status:</Typography>
            {mfaStatus.mfaEnabled ? (
              <Chip
                icon={<CheckCircle />}
                label="Enabled"
                sx={{ bgcolor: '#4caf5030', color: '#4caf50', '& .MuiChip-icon': { color: '#4caf50' } }}
              />
            ) : (
              <Chip
                icon={<Cancel />}
                label="Disabled"
                sx={{ bgcolor: '#ff6b6b30', color: '#ff6b6b', '& .MuiChip-icon': { color: '#ff6b6b' } }}
              />
            )}
            {mfaStatus.mfaRequired && (
              <Chip
                label="Required by admin"
                size="small"
                sx={{ bgcolor: '#ff980020', color: '#ff9800' }}
              />
            )}
          </Box>

          <Divider sx={{ mb: 3 }} />

          {mfaStatus.mfaEnabled ? (
            <Box>
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  MFA is active. You will be asked for a 6-digit code from your authenticator app each time you log in.
                </Typography>
              </Alert>
              {!mfaStatus.mfaRequired ? (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    setDisablePassword('');
                    setDisableDialogOpen(true);
                  }}
                  sx={{ borderColor: '#ff6b6b', color: '#ff6b6b' }}
                >
                  Disable MFA
                </Button>
              ) : (
                <Alert severity="info">
                  <Typography variant="body2">
                    MFA is required for your account by your administrator and cannot be disabled.
                  </Typography>
                </Alert>
              )}
            </Box>
          ) : (
            <Box>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  Enable MFA to protect your account with a 6-digit one-time code.
                  You will need an authenticator app such as Google Authenticator, Authy, or Microsoft Authenticator.
                </Typography>
              </Alert>
              <Button
                variant="contained"
                startIcon={<QrCodeIcon />}
                onClick={handleStartSetup}
                disabled={setupLoading}
                sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#388e3c' } }}
              >
                {setupLoading ? 'Setting up...' : 'Enable MFA'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* MFA Setup Dialog */}
      <Dialog
        open={setupDialogOpen}
        onClose={() => setSetupDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon sx={{ color: '#4caf50' }} />
            <Typography variant="h6">Set Up Two-Factor Authentication</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {setupData && (
            <Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Step 1:</strong> Scan this QR code with your authenticator app (Google Authenticator, Authy, Microsoft Authenticator, etc.)
              </Typography>

              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Box
                  component="img"
                  src={setupData.qrCode}
                  alt="MFA QR Code"
                  sx={{
                    width: 256,
                    height: 256,
                    borderRadius: 2,
                    border: '2px solid var(--rs-border)',
                  }}
                />
              </Box>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Can't scan?</strong> Manually enter this key in your app:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    bgcolor: 'var(--rs-bg)',
                    p: 1,
                    borderRadius: 1,
                    mt: 1,
                    wordBreak: 'break-all',
                    color: '#22d3ee',
                    fontWeight: 'bold',
                  }}
                >
                  {setupData.secret}
                </Typography>
              </Alert>

              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Step 2:</strong> Enter the 6-digit code shown in your authenticator app to verify setup:
              </Typography>

              <TextField
                fullWidth
                label="Verification Code"
                value={verifyCode}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setVerifyCode(val);
                }}
                placeholder="000000"
                inputProps={{
                  maxLength: 6,
                  style: {
                    textAlign: 'center',
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    letterSpacing: '0.5em',
                    fontFamily: 'monospace',
                  },
                }}
                sx={{ mt: 1 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSetupDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleVerifySetup}
            variant="contained"
            disabled={setupLoading || verifyCode.length !== 6}
            sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#388e3c' } }}
          >
            {setupLoading ? 'Verifying...' : 'Verify & Enable'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disable MFA Dialog */}
      <Dialog
        open={disableDialogOpen}
        onClose={() => setDisableDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" color="error">Disable MFA</Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Disabling MFA will reduce the security of your account. Enter your password to confirm.
            </Typography>
          </Alert>
          <TextField
            fullWidth
            label="Current Password"
            type="password"
            value={disablePassword}
            onChange={(e) => setDisablePassword(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisableDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDisableMfa}
            variant="contained"
            color="error"
            disabled={setupLoading || !disablePassword}
          >
            {setupLoading ? 'Disabling...' : 'Disable MFA'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
